// OperationalSearchModal — cross-cutting call browser used by
// Main Dashboard, Manager Ops, and My Coaching. Filters honour the
// caller's site-scope automatically (backed by /api/calls + agent picker
// from /api/analytics/agents/directory).
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, X, ShieldAlert, Voicemail, ArrowRight, Filter } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MANAGER_ROLES = new Set([
  'master_admin', 'admin', 'manager', 'domain_admin',
  'domain_supervisor', 'supervisor', 'office_manager',
]);

const scoreBandColor = (score) => {
  if (score == null) return 'bg-slate-100 text-slate-500 border-slate-200';
  if (score >= 80) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (score >= 60) return 'bg-teal-50 text-teal-700 border-teal-200';
  if (score >= 40) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-rose-50 text-rose-700 border-rose-200';
};

const fmtDate = (v) => {
  if (!v) return '—';
  try {
    const d = new Date(v);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const InlineChip = ({ children, tone = 'slate', 'data-testid': tid }) => {
  const tones = {
    slate: 'bg-slate-50 text-slate-600 border-slate-200',
    rose: 'bg-rose-50 text-rose-700 border-rose-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
  };
  return (
    <span data-testid={tid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${tones[tone]}`}>
      {children}
    </span>
  );
};

export const OperationalSearchButton = ({ variant = 'default', 'data-testid': testId = 'operational-search-btn' }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  // Available to admin + supervisor roles only. Individual agents don't
  // need this — their calls list already restricts to their own extension.
  if (!user || !MANAGER_ROLES.has(user.role)) return null;
  const cls = variant === 'ghost'
    ? 'inline-flex items-center gap-1.5 text-sm text-teal-700 hover:text-teal-800 hover:bg-teal-50 rounded-lg px-2.5 py-1.5 border border-teal-200'
    : 'inline-flex items-center gap-1.5 text-sm text-white bg-slate-900 hover:bg-slate-800 rounded-lg px-3 py-1.5 font-medium';
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={testId} className={cls}>
          <Search className="w-4 h-4" />
          Operational Search
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="op-search-modal">
        <OperationalSearchPanel onClose={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
};

const OperationalSearchPanel = ({ onClose }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Default 30-day window
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [agentId, setAgentId] = useState('');
  const [complianceOnly, setComplianceOnly] = useState(false);
  const [callType, setCallType] = useState('');
  const [scoreMin, setScoreMin] = useState('');
  const [scoreMax, setScoreMax] = useState('');
  const [q, setQ] = useState('');
  const [excludeVM, setExcludeVM] = useState(true);

  const [agents, setAgents] = useState([]);
  const [siteScoped, setSiteScoped] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ran, setRan] = useState(false);

  // Load agent directory on mount
  useEffect(() => {
    axios.get(`${API}/analytics/agents/directory`, { headers })
      .then(r => {
        setAgents(r.data?.agents || []);
        setSiteScoped(!!r.data?.site_scoped);
      })
      .catch(() => setAgents([]));
  }, [headers]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (dateFrom) p.set('date_from', new Date(`${dateFrom}T00:00:00Z`).toISOString());
      if (dateTo) p.set('date_to', new Date(`${dateTo}T23:59:59Z`).toISOString());
      if (agentId) p.set('agent_id', agentId);
      if (complianceOnly) p.set('has_violation', '1');
      if (callType) p.set('call_type', callType);
      if (scoreMin !== '') p.set('score_min', String(scoreMin));
      if (scoreMax !== '') p.set('score_max', String(scoreMax));
      if (q.trim()) p.set('q', q.trim());
      if (excludeVM) p.set('exclude_voicemail', '1');
      p.set('limit', '200');
      const r = await axios.get(`${API}/calls?${p.toString()}`, { headers });
      setRows(r.data || []);
      setRan(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Search failed');
    } finally { setLoading(false); }
  }, [dateFrom, dateTo, agentId, complianceOnly, callType, scoreMin, scoreMax, q, excludeVM, headers]);

  const clearAll = () => {
    setAgentId(''); setComplianceOnly(false); setCallType('');
    setScoreMin(''); setScoreMax(''); setQ(''); setExcludeVM(true);
  };

  const complianceCount = rows.filter(r => r.has_governance_violation).length;

  return (
    <div className="flex flex-col h-[80vh]">
      <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-200">
        <DialogTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Search className="w-5 h-5 text-teal-600" />
          Operational Search
          {siteScoped && (
            <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
              Site-scoped view
            </span>
          )}
        </DialogTitle>
        <p className="text-xs text-slate-500 mt-1">
          Browse every call in your scope. Combine agent + date + score + text to drill in.
          Flip <span className="font-semibold">Compliance only</span> to see just the violations.
        </p>
      </DialogHeader>

      {/* Filter bar */}
      <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
        <div className="col-span-2 md:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Search text</label>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Phone, agent name…"
            className="w-full mt-1 px-3 py-1.5 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
            data-testid="op-search-q"
            onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 rounded-lg border border-slate-200"
            data-testid="op-search-date-from" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 rounded-lg border border-slate-200"
            data-testid="op-search-date-to" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Agent</label>
          <select value={agentId} onChange={e => setAgentId(e.target.value)}
            className="w-full mt-1 px-2 py-1.5 rounded-lg border border-slate-200 bg-white"
            data-testid="op-search-agent">
            <option value="">All agents{siteScoped ? ' (in your scope)' : ''}</option>
            {agents.map(a => (
              <option key={a.agent_id} value={a.agent_id}>{a.name} · {a.extension}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Score</label>
          <div className="flex gap-1 mt-1">
            <input type="number" min={0} max={100} placeholder="min" value={scoreMin}
              onChange={e => setScoreMin(e.target.value)}
              className="w-1/2 px-2 py-1.5 rounded-lg border border-slate-200"
              data-testid="op-search-score-min" />
            <input type="number" min={0} max={100} placeholder="max" value={scoreMax}
              onChange={e => setScoreMax(e.target.value)}
              className="w-1/2 px-2 py-1.5 rounded-lg border border-slate-200"
              data-testid="op-search-score-max" />
          </div>
        </div>

        <div className="col-span-2 md:col-span-6 flex items-center flex-wrap gap-4 pt-1">
          <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={complianceOnly}
              onChange={e => setComplianceOnly(e.target.checked)}
              data-testid="op-search-compliance-only" />
            <ShieldAlert className="w-3.5 h-3.5 text-rose-500" />
            Compliance violations only
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={excludeVM}
              onChange={e => setExcludeVM(e.target.checked)}
              data-testid="op-search-hide-vm" />
            Hide voicemails
          </label>
          <select value={callType} onChange={e => setCallType(e.target.value)}
            className="text-xs px-2 py-1 rounded border border-slate-200 bg-white"
            data-testid="op-search-call-type">
            <option value="">All call types</option>
            <option value="initial_sales">Initial sales</option>
            <option value="follow_up">Follow-up</option>
            <option value="non_sales">Non-sales</option>
          </select>
          <div className="flex-1" />
          <button onClick={clearAll}
            className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
            data-testid="op-search-clear">
            <X className="w-3 h-3" />
            Clear
          </button>
          <button onClick={runSearch} disabled={loading}
            className="inline-flex items-center gap-1.5 text-sm text-white bg-teal-600 hover:bg-teal-500 disabled:opacity-50 rounded-lg px-4 py-1.5 font-medium"
            data-testid="op-search-run">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />}
            Search
          </button>
        </div>
      </div>

      {/* Result summary */}
      {ran && (
        <div className="px-6 py-2 text-xs text-slate-500 border-b border-slate-100 bg-white flex items-center gap-3">
          <span data-testid="op-search-total"><span className="font-semibold text-slate-800">{rows.length}</span> call{rows.length === 1 ? '' : 's'}</span>
          {complianceCount > 0 && (
            <InlineChip tone="rose" data-testid="op-search-compliance-count">
              <ShieldAlert className="w-3 h-3" />
              {complianceCount} with violations
            </InlineChip>
          )}
        </div>
      )}

      {/* Results */}
      <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="op-search-results">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>
        ) : !ran ? (
          <p className="text-center text-slate-400 italic py-16" data-testid="op-search-empty">Set filters and hit Search to browse calls.</p>
        ) : rows.length === 0 ? (
          <p className="text-center text-slate-400 italic py-16" data-testid="op-search-no-results">No calls matched. Widen the date range or clear filters.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <tr>
                <th className="text-left py-2">When</th>
                <th className="text-left py-2">Agent</th>
                <th className="text-left py-2">Caller</th>
                <th className="text-left py-2">Type</th>
                <th className="text-left py-2">Score</th>
                <th className="text-left py-2">Flags</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c) => (
                <tr
                  key={c.call_id}
                  className={`hover:bg-slate-50 cursor-pointer ${c.has_governance_violation ? 'bg-rose-50/30' : ''}`}
                  onClick={() => { onClose?.(); navigate(`/calls/${c.call_id}`); }}
                  data-testid={`op-search-row-${c.call_id}`}
                >
                  <td className="py-2 text-slate-600">{fmtDate(c.call_date)}</td>
                  <td className="py-2 text-slate-800 font-medium">{c.agent_name || c.agent_id}</td>
                  <td className="py-2 text-slate-600">{c.customer_phone || '—'}</td>
                  <td className="py-2 text-slate-500 text-xs">{c.call_type || '—'}</td>
                  <td className="py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${scoreBandColor(c.overall_score)}`}>
                      {c.overall_score != null ? c.overall_score : '—'}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-1 flex-wrap">
                      {c.has_governance_violation && (
                        <InlineChip tone="rose" data-testid={`op-badge-compliance-${c.call_id}`}>
                          <ShieldAlert className="w-3 h-3" />
                          Compliance
                        </InlineChip>
                      )}
                      {c.won && (
                        <InlineChip tone="teal">Won</InlineChip>
                      )}
                      {(c.is_voicemail || c.is_voicemail_screened) && (
                        <InlineChip tone="purple">
                          <Voicemail className="w-3 h-3" />VM
                        </InlineChip>
                      )}
                    </div>
                  </td>
                  <td className="py-2 text-right">
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default OperationalSearchButton;
